'use client';

import { ChevronDown } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import type { HeroProps } from '@routeroyal/theme-core';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

/**
 * Stone Investment Hero Component
 * 
 * Concept One Design (Exact Match):
 * - Full-screen video background with fallback image
 * - Gradient overlays for text contrast
 * - "The Art of Living" with italic "of"
 * - font-serif for headings, font-sans for body
 * - Dual soft CTAs under subtitle + scroll indicator
 * 
 * Note: This component only renders the hero section.
 * Use IntroductionSection, SignatureSelection, and FooterTeaser
 * separately on the landing page for proper component composition.
 */
export function StoneInvestmentHero({ 
  title, 
  subtitle, 
  backgroundImage,
  ctaText,
  ctaLink,
  messages,
  locale = 'en',
  data: _data,
}: HeroProps) {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  // Get stone-specific translations
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const stoneHeroMessages = (stoneMessages.hero || {}) as Record<string, string>;
  const stoneBrandMessages = (stoneMessages.brand || {}) as Record<string, string>;
  
  // Also check general hero messages for backwards compatibility
  const heroMessages = messages?.hero as Record<string, unknown> | undefined;
  const heroTitle = title || stoneHeroMessages.title || (heroMessages?.title as string) || 'The Art of Living';
  const heroSubtitle = subtitle || stoneHeroMessages.subtitle || (heroMessages?.subtitle as string) || 'Exceptional residences in Mauritius and Provence.';
  const discoverText = stoneHeroMessages.discover || 'Discover';
  const brandName = stoneBrandMessages.name || 'Stone Investment';
  const accessMessages = (stoneMessages.accessibility || {}) as Record<string, string>;
  const heroAlt = accessMessages.heroBackgroundAlt || 'Hero Background';

  const primaryCtaText = stoneHeroMessages.primaryCta || ctaText || 'Explore the Collection';
  const primaryCtaHref = ctaLink || `/${locale}/properties`;
  const secondaryCtaText = stoneHeroMessages.secondaryCta || 'Private Consultation';
  const secondaryCtaHref = `/${locale}/contact?intent=consultation`;

  const staticImage = backgroundImage || 'https://res.cloudinary.com/elementary-media/video/upload/so_0,f_auto,q_auto,w_1920/v1769763005/worlds_apart__one_standard_full_ick3gg.jpg';
  const blurPlaceholder = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxNiA5Ij48ZmlsdGVyIGlkPSJiIiBjb2xvci1pbnRlcnBvbGF0aW9uLWZpbHRlcnM9InNSR0IiPjxmZUdhdXNzaWFuQmx1ciBzdGREZXZpYXRpb249IjMiIC8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjE2IiBoZWlnaHQ9IjkiIGZpbGw9IiM0YTNmMzUiIGZpbHRlcj0idXJsKCNiKSIgLz48cmVjdCB4PSIwIiB5PSIwIiB3aWR0aD0iMTYiIGhlaWdodD0iNSIgZmlsbD0iIzZhOGZiNSIgb3BhY2l0eT0iMC4zIiBmaWx0ZXI9InVybCgjYikiIC8+PHJlY3QgeD0iNSIgeT0iMyIgd2lkdGg9IjYiIGhlaWdodD0iNCIgZmlsbD0iI2Q0YzViMCIgb3BhY2l0eT0iMC40IiBmaWx0ZXI9InVybCgjYikiIC8+PC9zdmc+';

  return (
    <header
      className="relative w-full h-dvh overflow-hidden"
      data-dark-hero="true"
      style={{ backgroundImage: `url("${blurPlaceholder}")`, backgroundSize: 'cover', backgroundColor: '#1c1917' }}
    >
      {/* Full-res image — loads over the inline blur background */}
      {!isVideoLoaded && (
        <div className="absolute inset-0">
          <Image
            src={staticImage}
            alt={heroAlt}
            fill
            className="object-cover opacity-70"
            priority
            fetchPriority="high"
            sizes="100vw"
          />
        </div>
      )}

      {/* Video layer — no poster to avoid duplicate image download */}
      <div className={`absolute inset-0 transition-opacity duration-1000 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}>
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-full object-cover opacity-70 scale-105"
          onLoadedData={() => setIsVideoLoaded(true)}
        >
          <source 
            src="https://res.cloudinary.com/elementary-media/video/upload/f_auto,q_auto,vc_auto,w_1920/v1769763005/worlds_apart__one_standard_full_ick3gg.mp4" 
            type="video/mp4" 
          />
        </video>
      </div>

      {/* Dark overlay for text contrast */}
      <div className="absolute inset-0 bg-linear-to-b from-stone-900/50 via-stone-900/40 to-stone-900/80" />

      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-6">
        <div className="mb-8">
          <p className="font-sans text-xs md:text-sm uppercase tracking-[0.4em]">
            {brandName}
          </p>
        </div>

        <h1 
          className="font-serif text-6xl md:text-9xl text-white! leading-tight [text-shadow:0_2px_20px_rgba(0,0,0,0.6)]"
        >
          {heroTitle.includes('of') ? (
            <>
              {heroTitle.split('of')[0]}
              <span className="italic font-light opacity-80">of</span>
              {heroTitle.split('of')[1]}
            </>
          ) : heroTitle}
        </h1>
              
        <p 
          className="hero-description mt-8 font-sans font-light text-lg md:text-xl text-stone-200 max-w-xl"
          data-speakable
        >
          {heroSubtitle}
        </p>

        {/* Dual soft CTAs — quiet, tracked, Concept One */}
        <div className="mt-10 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          <Link
            href={primaryCtaHref}
            onClick={() => analyticsEvents.custom('cta_click', {
              cta_text: primaryCtaText,
              cta_location: 'hero',
              cta_destination: primaryCtaHref,
              cta_variant: 'primary',
            })}
            className="inline-flex items-center justify-center px-8 py-3.5 border border-white/80 text-white text-xs uppercase tracking-[0.15em] font-sans hover:bg-white hover:text-stone-900 transition-all duration-500"
          >
            {primaryCtaText}
          </Link>
          <Link
            href={secondaryCtaHref}
            onClick={() => analyticsEvents.custom('cta_click', {
              cta_text: secondaryCtaText,
              cta_location: 'hero',
              cta_destination: secondaryCtaHref,
              cta_variant: 'secondary',
            })}
            className="inline-flex items-center justify-center text-xs uppercase tracking-[0.15em] font-sans text-stone-200 border-b border-stone-200/60 pb-0.5 hover:text-white hover:border-white transition-colors duration-500"
          >
            {secondaryCtaText}
          </Link>
        </div>

        <div className="absolute bottom-12">
          <div className="flex flex-col items-center gap-2 animate-bounce opacity-60">
            <span className="text-xs uppercase tracking-widest font-sans">{discoverText}</span>
            <ChevronDown size={20} aria-hidden="true" />
          </div>
        </div>
      </div>
    </header>
  );
}
